import { useSyncExternalStore } from "react";

export interface Store<T> {
  get(): T;
  set(next: T): void;
  subscribe(fn: () => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      value = next;
      for (const fn of listeners) fn();
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
  };
}

export function useStore<T>(store: Store<T>): T {
  return useSyncExternalStore(store.subscribe, store.get, store.get);
}

/** Immutably set a dotted path, e.g. setPath(theme, 'palette.rose', '#f00'). */
export function setPath<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split(".");
  // Arrays are cloned as arrays so a numeric path segment stays an index —
  // rankPoints.3 must not turn the array into an object.
  const clone = (
    Array.isArray(obj)
      ? [...(obj as unknown[])]
      : { ...(obj as Record<string, unknown>) }
  ) as Record<string, unknown>;
  let cursor = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]!;
    const child = cursor[key];
    cursor[key] = Array.isArray(child)
      ? [...child]
      : { ...(child as Record<string, unknown>) };
    cursor = cursor[key] as Record<string, unknown>;
  }
  cursor[keys[keys.length - 1]!] = value;
  return clone as T;
}

export function getPath(obj: unknown, path: string): unknown {
  return path
    .split(".")
    .reduce<unknown>((acc, k) => (acc as Record<string, unknown>)?.[k], obj);
}
